import React, { useState, useEffect, useRef } from "react";
import * as GLM from "./webgl0.js";
import { gl_start, registerSceneHMR, setUniform } from "./webgl0.js";

function Scene() {

this.vertexShader = `\
#version 300 es
in  vec3 aPos;
out vec3 vPos;
void main() {
   gl_Position = vec4(aPos, 1.);
   vPos = aPos;
}`;

this.fragmentShader = `\
#version 300 es
precision highp float;
uniform float uTime;
uniform vec3 uViewPoint;

uniform mat4 uQ[6];

in  vec3 vPos;
out vec4 fragColor;

vec3 rayEq(vec3 V, vec3 W, mat4 Q) {

   float A = Q[0].x, B = Q[1].x+Q[0].y, C = Q[2].x+Q[0].z, D = Q[3].x+Q[0].w,
                     E = Q[1].y       , F = Q[2].y+Q[1].z, G = Q[3].y+Q[1].w,
                                        H = Q[2].z       , I = Q[3].z+Q[2].w,
                                                           J = Q[3].w       ;

   float a = A * W.x * W.x +
             B * W.x * W.y +
             C * W.z * W.x +
             E * W.y * W.y +
             F * W.y * W.z +
             H * W.z * W.z ;

   float b = 2. * A * V.x * W.x +
                  B * (W.x * V.y + V.y * W.x) +
                  C * (V.z * W.x + V.x * W.z) +
                  D * W.x +
             2. * E * V.y * W.y +
                  F * (V.y * W.z + V.z * W.y) +
                  G * W.y +
             2. * H * V.z * W.z +
                  I * W.z;

   float c = A * V.x * V.x +
             B * V.x * V.y +
             C * V.z * V.x +
             D * V.x       +
             E * V.y * V.y +
             F * V.y * V.z +
             G * V.y       +
             H * V.z * V.z +
             I * V.z       +
             J;

   return vec3(a,b,c);
}

vec2 findRoots(vec3 eq) {
   float a = eq.x, b = eq.y, c = eq.z;
   vec2 t = vec2(-1.);
   float discr = b * b - 4. * a * c;
   if (discr >= 0.)
      t = vec2(-b - sqrt(discr), -b + sqrt(discr)) / (2. * a);
   return t;
}

vec3 normalQ(mat4 Q, vec3 P) {

   float A = Q[0].x, B = Q[1].x+Q[0].y, C = Q[2].x+Q[0].z, D = Q[3].x+Q[0].w,
                     E = Q[1].y       , F = Q[2].y+Q[1].z, G = Q[3].y+Q[1].w,
                                        H = Q[2].z       , I = Q[3].z+Q[2].w,
                                                           J = Q[3].w       ;

   return normalize(vec3(2. * A * P.x + C * P.z + B * P.y + D,
                         2. * E * P.y + F * P.z + B * P.x + G,
                         2. * H * P.z + F * P.y + C * P.x + I));
}

void main() {
   fragColor = vec4(0.);

   vec3 V = uViewPoint;
   vec3 W = normalize(vPos-V);

   vec3 color1 = vec3(0.);
   vec2 tI1 = vec2(-1.,1000.);
   for (int i = 0 ; i < 3 ; i++) {
      vec2 tQ = findRoots(rayEq(V, W, uQ[i]));
      if (tQ.x > tI1.x) {
         vec3 N = normalQ(uQ[i], V + tQ.x * W);
         color1 = vec3(.1 + max(0., dot(N, vec3(.5))));
	 tI1.x = tQ.x;
      }
      if (tQ.y < tI1.y)
         tI1.y = tQ.y;
   }

   if (tI1.x > 0. && tI1.x < tI1.y)
      fragColor = vec4(sqrt(color1), 1.);


// SECOND SHAPE
   vec3 color2 = vec3(0.);
   vec2 tI2 = vec2(-1.,1000.);
   for (int i = 3 ; i < 6 ; i++) {
      vec2 tQ = findRoots(rayEq(V, W, uQ[i]));
      if (tQ.x > tI2.x) {
         vec3 N = normalQ(uQ[i], V + tQ.x * W);
         color2 = vec3(.1 + max(0., dot(N, vec3(.5))));
	 tI2.x = tQ.x;
      }
      if (tQ.y < tI2.y)
         tI2.y = tQ.y;
   }


   if (tI2.x > 0. && tI2.x < tI2.y && tI2.x < tI1.x)
      fragColor = vec4(sqrt(color2).r, 0.0 ,0.0, 1.);

}`;

let mxm = (a,b) => {
   let m = [];
   for (let c = 0 ; c < 16 ; c += 4)
   for (let r = 0 ; r < 4 ; r++)
      m.push( a[r]*b[c] + a[r+4]*b[c+1] + a[r+8]*b[c+2] + a[r+12]*b[c+3] );
   return m;
}

let transpose = m => [ m[0],m[4],m[ 8],m[12],
                       m[1],m[5],m[ 9],m[13],
                       m[2],m[6],m[10],m[14],
                       m[3],m[7],m[11],m[15] ];

let inverse = src => {
   let dst = [], det = 0, cofactor = (c, r) => {
      let s = (i, j) => src[c+i & 3 | (r+j & 3) << 2];
      return (c+r & 1 ? -1 : 1) * ( (s(1,1)*(s(2,2)*s(3,3)-s(3,2)*s(2,3)))
                                  - (s(2,1)*(s(1,2)*s(3,3)-s(3,2)*s(1,3)))
                                  + (s(3,1)*(s(1,2)*s(2,3)-s(2,2)*s(1,3))) );
   }
   for (let n = 0 ; n < 16 ; n++) dst.push(cofactor(n >> 2, n & 3));
   for (let n = 0 ; n <  4 ; n++) det += src[n] * dst[n << 2];
   for (let n = 0 ; n < 16 ; n++) dst[n] /= det;
   return dst;
}

let qxm = (Q,M) => {
   let MI = inverse(M);
   return mxm(transpose(MI), mxm(Q, MI));
}

let startTime = Date.now()/1000;

this.update = ( viewPoint = [0, 0, 7] ) => {
   let time = Date.now()/1000 - startTime;
   setUniform('1f', 'uTime', time);
   setUniform('3fv', 'uViewPoint', viewPoint);

   let m1 = mxm(GLM.turnY(time),GLM.scale(.2));
   let m2 = mxm(GLM.move(0, 0, 2), mxm(GLM.turnY(time),GLM.scale(.1)));
   // let m2 = mxm(move(0, 0, .8),mxm(turnY(time),scale(.15)));
   setUniform('Matrix4fv', 'uQ', false,
      new Float32Array([
         qxm(GLM.slabX,m1), qxm(GLM.slabY,m1), qxm(GLM.slabZ,m1),
         qxm(GLM.tubeZ,m2), qxm(GLM.slabZ,m2), qxm(GLM.slabZ,m2)
      ].flat()));
}
}

export default function WebGLCanvas() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    try { if (typeof canvas.stopRender === "function") canvas.stopRender(); } catch (e) {}

    const newScene = new Scene();

    gl_start(canvas, newScene);

    try { registerSceneHMR(module, canvas, () => new Scene()); } catch (e) { /* ignore */ }
    return () => { try { if (typeof canvas.stopRender === "function") canvas.stopRender(); } catch (e) {} };
  }, []);

  return (
    <div style={{ display: "flex", justifyContent: "center", gap: "16px" }}>
      <canvas ref={canvasRef} width={400} height={400}
        style={{ width: "400px", height: "400px", display: "block", margin: "0 auto", background: "black" }} />
        <pre className = "codeblock-shader">{ String(Scene) }</pre>
    </div>
  );
}
